import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AcademicYear, YearState, UserRole } from '@/types';
import { academicYearApi } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';

type AcademicYearSelectionContextType = {
  academicYears: AcademicYear[];
  activeAcademicYear: AcademicYear | null;
  selectedAcademicYearId: string;
  selectedAcademicYear: AcademicYear | null;
  canSelectHistoricalYear: boolean;
  isLoadingYears: boolean;
  setSelectedAcademicYearId: (yearId: string) => void;
  refreshAcademicYears: () => Promise<AcademicYear[]>;
};

const AcademicYearSelectionContext = createContext<AcademicYearSelectionContextType | undefined>(undefined);

const STORAGE_KEY = 'academic_year_view_id';

const HISTORICAL_ROLES = new Set<UserRole>([UserRole.SUPER_ADMIN, UserRole.COORDINATOR, UserRole.DIRECTOR]);

export function useAcademicYearSelection() {
  const context = useContext(AcademicYearSelectionContext);
  if (!context) {
    throw new Error('useAcademicYearSelection debe ser usado dentro de AcademicYearProvider');
  }
  return context;
}

interface AcademicYearProviderProps {
  children: ReactNode;
}

export function AcademicYearProvider({ children }: AcademicYearProviderProps) {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [selectedAcademicYearId, setSelectedAcademicYearIdState] = useState('');
  const [isLoadingYears, setIsLoadingYears] = useState(false);
  const refreshPromiseRef = useRef<Promise<AcademicYear[]> | null>(null);

  const canSelectHistoricalYear = Boolean(user?.role && HISTORICAL_ROLES.has(user.role as UserRole));

  const activeAcademicYear = useMemo(
    () => academicYears.find((year) => year.state === YearState.ACTIVE) || null,
    [academicYears]
  );

  const selectedAcademicYear = useMemo(
    () => academicYears.find((year) => year.id === selectedAcademicYearId) || null,
    [academicYears, selectedAcademicYearId]
  );

  const syncSelectedYear = (nextYearId: string, persist = true) => {
    setSelectedAcademicYearIdState(nextYearId);
    if (persist) {
      if (nextYearId) {
        localStorage.setItem(STORAGE_KEY, nextYearId);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  };

  const refreshAcademicYears = async (): Promise<AcademicYear[]> => {
    if (!isAuthenticated || isAuthLoading) {
      return [];
    }

    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    let loadedYears: AcademicYear[] = [];
    const request = (async () => {
      setIsLoadingYears(true);
      try {
        const response = await academicYearApi.list({ page: 1, pageSize: 100 });
        if (!response?.success) {
          throw new Error(response?.message || 'No se pudieron cargar los años escolares');
        }

        const years = response.data?.items || response.data || [];
        loadedYears = Array.isArray(years) ? (years as AcademicYear[]) : [];
        setAcademicYears(loadedYears);

        const storedYearId = localStorage.getItem(STORAGE_KEY) || '';
        const storedYearExists = storedYearId ? loadedYears.some((year) => year.id === storedYearId) : false;
        const activeYearId = loadedYears.find((year) => year.state === YearState.ACTIVE)?.id || '';
        const fallbackYearId = activeYearId || loadedYears[0]?.id || '';

        if (canSelectHistoricalYear) {
          syncSelectedYear(storedYearExists ? storedYearId : fallbackYearId, false);
        } else {
          syncSelectedYear(fallbackYearId, false);
        }
      } catch (error) {
        console.error('Error al cargar años escolares compartidos:', error);
        setAcademicYears([]);
        syncSelectedYear('', false);
        return [];
      } finally {
        setIsLoadingYears(false);
      }

      return loadedYears;
    })();

    refreshPromiseRef.current = request;
    try {
      return await request;
    } finally {
      refreshPromiseRef.current = null;
    }
  };

  useEffect(() => {
    if (!isAuthenticated || isAuthLoading) {
      setAcademicYears([]);
      setSelectedAcademicYearIdState('');
      return;
    }

    void refreshAcademicYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAuthLoading, user?.role]);

  useEffect(() => {
    if (!academicYears.length) return;

    if (canSelectHistoricalYear) {
      const storedYearId = localStorage.getItem(STORAGE_KEY) || '';
      if (storedYearId && academicYears.some((year) => year.id === storedYearId)) {
        if (storedYearId !== selectedAcademicYearId) {
          syncSelectedYear(storedYearId, false);
        }
        return;
      }

      if (!selectedAcademicYearId && activeAcademicYear?.id) {
        syncSelectedYear(activeAcademicYear.id, false);
      }
      return;
    }

    if (activeAcademicYear?.id && selectedAcademicYearId !== activeAcademicYear.id) {
      syncSelectedYear(activeAcademicYear.id, false);
    }
  }, [academicYears, activeAcademicYear?.id, canSelectHistoricalYear, selectedAcademicYearId]);

  const setSelectedAcademicYearId = (yearId: string) => {
    if (!yearId) {
      syncSelectedYear('', true);
      return;
    }

    const exists = academicYears.some((year) => year.id === yearId);
    if (!exists) return;

    if (!canSelectHistoricalYear) {
      const activeYearId = activeAcademicYear?.id || academicYears[0]?.id || '';
      syncSelectedYear(activeYearId, true);
      return;
    }

    syncSelectedYear(yearId, true);
  };

  const value: AcademicYearSelectionContextType = {
    academicYears,
    activeAcademicYear,
    selectedAcademicYearId,
    selectedAcademicYear,
    canSelectHistoricalYear,
    isLoadingYears,
    setSelectedAcademicYearId,
    refreshAcademicYears,
  };

  return (
    <AcademicYearSelectionContext.Provider value={value}>
      {children}
    </AcademicYearSelectionContext.Provider>
  );
}
